/**
 * @fileoverview React Query hook for fetching change orders data
 *
 * Provides data fetching, caching, and state management for the
 * ClientChangeOrders component using React Query and Firebase Functions.
 *
 * @requires react-query
 * @requires firebase/functions
 * @version 1.0.0
 * @created 2025-12-03
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Custom hook to fetch change orders for a project
 *
 * @param {string} projectId - Airtable project record ID
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with data, loading, and error states
 *
 * @example
 * const { data, isLoading, error } = useChangeOrders(projectId);
 * // data.changeOrders - Array of change order objects
 */
export const useChangeOrders = (projectId, options = {}) => {
  return useQuery({
    queryKey: ["changeOrders", projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }

      const getChangeOrders = httpsCallable(functions, "getChangeOrders");
      const result = await getChangeOrders({ projectId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch change orders");
      }

      return result.data.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
};

/**
 * Custom hook to fetch a single change order by ID
 *
 * @param {string} changeOrderId - Airtable change order record ID
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with change order data
 *
 * @example
 * const { data, isLoading, error } = useChangeOrder(changeOrderId);
 */
export const useChangeOrder = (changeOrderId, options = {}) => {
  return useQuery({
    queryKey: ["changeOrder", changeOrderId],
    queryFn: async () => {
      if (!changeOrderId) {
        throw new Error("Change Order ID is required");
      }

      const getChangeOrderById = httpsCallable(
        functions,
        "getChangeOrderById"
      );
      const result = await getChangeOrderById({ changeOrderId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch change order");
      }

      return result.data.data;
    },
    enabled: !!changeOrderId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    ...options,
  });
};

/**
 * Custom hook to fetch change order statistics for a project
 *
 * @param {string} projectId - Airtable project record ID
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with statistics data
 *
 * @example
 * const { data: stats } = useChangeOrderStats(projectId);
 * // stats.total, stats.approved, stats.pending, etc.
 */
export const useChangeOrderStats = (projectId, options = {}) => {
  return useQuery({
    queryKey: ["changeOrderStats", projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }

      const getChangeOrderStats = httpsCallable(
        functions,
        "getChangeOrderStats"
      );
      const result = await getChangeOrderStats({ projectId });

      if (!result.data.success) {
        throw new Error(
          result.data.error || "Failed to fetch change order stats"
        );
      }

      return result.data.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    ...options,
  });
};

/**
 * Custom hook to fetch change order field options (status, reason)
 * from the Airtable Meta API.
 *
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with field options
 *
 * @example
 * const { data: fieldOptions } = useChangeOrderFieldOptions();
 * // fieldOptions.status = ["Submitted", "Approved", ...]
 * // fieldOptions.reason = ["Client Request", ...]
 */
export const useChangeOrderFieldOptions = (options = {}) => {
  return useQuery({
    queryKey: ["changeOrderFieldOptions"],
    queryFn: async () => {
      const getChangeOrderFieldOptions = httpsCallable(
        functions,
        "getChangeOrderFieldOptions",
      );
      const result = await getChangeOrderFieldOptions();

      if (!result.data.success) {
        throw new Error(
          result.data.error || "Failed to fetch change order field options",
        );
      }

      return result.data.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour - field options rarely change
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
    ...options,
  });
};

/**
 * Mutation hook to create a new change order
 *
 * @param {string} projectId - Project ID for cache invalidation
 * @returns {Object} Mutation object with mutateAsync, isPending, etc.
 *
 * @example
 * const createCO = useCreateChangeOrder(projectId);
 * await createCO.mutateAsync({ title, description, ... });
 */
export const useCreateChangeOrder = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changeOrderData) => {
      const createChangeOrder = httpsCallable(functions, "createChangeOrder");
      const result = await createChangeOrder({ projectId, changeOrderData });

      if (!result.data.success) {
        throw new Error(
          result.data.error || "Failed to create change order",
        );
      }

      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["changeOrders", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["changeOrderStats", projectId],
      });
    },
  });
};

/**
 * Mutation hook to update an existing change order
 *
 * @param {string} projectId - Project ID for cache invalidation
 * @returns {Object} Mutation object with mutateAsync, isPending, etc.
 *
 * @example
 * const updateCO = useUpdateChangeOrder(projectId);
 * await updateCO.mutateAsync({ changeOrderId, updates: { title, ... } });
 */
export const useUpdateChangeOrder = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ changeOrderId, updates }) => {
      const updateChangeOrder = httpsCallable(functions, "updateChangeOrder");
      const result = await updateChangeOrder({ changeOrderId, updates });

      if (!result.data.success) {
        throw new Error(
          result.data.error || "Failed to update change order",
        );
      }

      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["changeOrders", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["changeOrderStats", projectId],
      });
    },
  });
};

export default useChangeOrders;
